import { Card, CardContent } from "@/components/ui/card";
import { Gauge, CheckCircle, ChevronRight } from "lucide-react";
import type {
  PhysicalDimension,
  PhysicalStrandPayload,
} from "@/data/sampleDemoResult";

/**
 * Shared Physical Strand results template used by both the public demo and the
 * logged-in assessment. Renders deterministic, spec-driven scores (no LLM): a
 * score-summary box, four metric tiles on net speaking time, and reproducible
 * Strengths / Next Steps sourced from the band-descriptor cells.
 *
 * Spec reference: OracyAI Physical Strand Scoring Spec, sections 5 and 6.
 */

const DIMENSION_ORDER: { key: keyof PhysicalStrandPayload["dimensions"]; label: string }[] = [
  { key: "pace", label: "Pace" },
  { key: "filler_words", label: "Filler words" },
  { key: "pause_distribution", label: "Pause distribution" },
  { key: "clarity", label: "Clarity" },
];

function titleCase(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function formatValue(dim: PhysicalDimension | null | undefined): string {
  // A dimension can be missing from the payload — guard so a stray null key can't
  // blow up the whole results view (white screen).
  if (!dim) return "—";
  // Confidence reads as a percentage (0.997 -> 99.7%) so it never rounds up to a
  // misleading "1.00". Pace 2dp syl/sec, filler 1dp per min, pause ratio 2dp.
  if (dim.unit === "avg confidence") {
    return `${(dim.value * 100).toFixed(1)}%`;
  }
  const decimals = dim.unit === "per minute" ? 1 : 2;
  return dim.value.toFixed(decimals);
}

export default function PhysicalStrandResults({
  physical,
}: {
  physical: PhysicalStrandPayload;
}) {
  const dims = physical.dimensions;

  return (
    <>
      {/* Score summary — sits at the top, above the transcript */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Gauge className="w-5 h-5 text-strand-physical" />
              Physical Strand Delivery
            </h2>
            <span className="text-sm font-medium px-3 py-1 rounded-full bg-strand-physical/10 text-strand-physical">
              Overall: {titleCase(physical.overall_band)}
            </span>
          </div>

          <div className="divide-y divide-border">
            {DIMENSION_ORDER.map(({ key, label }) => {
              const dim = dims?.[key];
              if (!dim) return null;
              return (
                <div
                  key={key}
                  className="flex flex-col items-start gap-1 py-2.5 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-0"
                >
                  <span className="text-foreground font-medium">{label}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-muted-foreground font-mono">
                      {formatValue(dim)} {dim.unit}
                    </span>
                    <span className="text-foreground font-semibold tabular-nums">
                      {dim.score}/5
                    </span>
                    <span className="text-xs text-muted-foreground w-24 text-right">
                      {titleCase(dim.label)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Metric tiles — the actual scoring inputs, all on net speaking time */}
      <Card>
        <CardContent className="p-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricTile value={formatValue(dims?.pace ?? null)} label="Syllables/sec" />
            <MetricTile value={formatValue(dims?.filler_words ?? null)} label="Fillers/min" />
            <MetricTile value={formatValue(dims?.pause_distribution ?? null)} label="Pause boundary ratio" />
            <MetricTile value={formatValue(dims?.clarity ?? null)} label="Average confidence" />
          </div>
        </CardContent>
      </Card>

      {/* Strengths + Next Steps (from band descriptors, not an LLM) */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-5">
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-success" />
              Strengths
            </h3>
            {physical.strengths.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No dimensions reached the effective level yet — see next steps.
              </p>
            ) : (
              <ul className="space-y-2">
                {physical.strengths.map((s, i) => (
                  <li
                    key={i}
                    className="text-sm text-muted-foreground flex items-start gap-2"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-success mt-1.5 shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-primary" />
              Next Steps
            </h3>
            {physical.next_steps.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Strong across every dimension — keep it up.
              </p>
            ) : (
              <ul className="space-y-2">
                {physical.next_steps.map((s, i) => (
                  <li
                    key={i}
                    className="text-sm text-muted-foreground flex items-start gap-2"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function MetricTile({ value, label }: { value: string; label: string }) {
  return (
    <div className="bg-secondary/50 rounded-lg p-3 text-center">
      <p className="text-2xl font-bold text-foreground font-mono">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );
}
